// material-ui
import { styled } from '@mui/material/styles';

// project imports
import { MenuOrientation, ThemeMode } from 'config';
import { appLayoutHeaderOffsetPx, drawerWidth, gridSpacing } from 'store/constant';
import { isNative } from 'platform/native';

interface MainStyleProps {
  open: boolean;
  menuOrientation: MenuOrientation;
  borderRadius: number;
}

// ==============================|| MAIN LAYOUT - STYLED ||============================== //

const MainContentStyled = styled('main', {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'menuOrientation' && prop !== 'borderRadius',
})<MainStyleProps>(({ theme, open, menuOrientation, borderRadius }) => {
  /** מסכי צ'אט / אשפים — גובה מתחת ל־header; ‎100dvh‎ מפחית חריגה מול ‎100vh‎ */
  const chatViewportMinusHeader = `calc(100dvh - ${
    menuOrientation === MenuOrientation.HORIZONTAL ? 135 : appLayoutHeaderOffsetPx
  }px)`;

  /** באפליקציית המובייל מסך הצ'אט תופס את כל הרוחב — בלי שולי הכרטיס של הווב */
  const chatGutter = isNative ? 0 : theme.spacing(gridSpacing);

  return {
  backgroundColor: theme.palette.mode === ThemeMode.DARK ? theme.palette.dark[800] : theme.palette.grey[100],
  minWidth: '1%',
  width: '100%',
  boxSizing: 'border-box',
  /** כמו הגרסה הקודמת (מינוס ‎appLayoutHeaderOffsetPx‎) — עכשיו עם ‎dvh‎ */
  minHeight: `calc(100dvh - ${appLayoutHeaderOffsetPx}px)`,
  flexGrow: 1,
  /** Single symmetric gutter — no extra marginInline so left/right match (Berry + gridSpacing). */
  padding: theme.spacing(gridSpacing),
  marginTop: appLayoutHeaderOffsetPx,
  borderRadius: `${borderRadius}px`,
  borderBottomLeftRadius: 0,
  borderBottomRightRadius: 0,
  ...(!open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.shorter + 200,
    }),
    [theme.breakpoints.up('md')]: {
      marginInlineStart:
        menuOrientation === MenuOrientation.VERTICAL ? -(drawerWidth - 72) : theme.spacing(gridSpacing),
      width: `calc(100% - ${drawerWidth}px)`,
      marginTop:
        menuOrientation === MenuOrientation.HORIZONTAL ? 135 : appLayoutHeaderOffsetPx,
    },
  }),
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.shorter + 200,
    }),
    marginInlineStart: menuOrientation === MenuOrientation.HORIZONTAL ? theme.spacing(gridSpacing) : 0,
    marginTop: menuOrientation === MenuOrientation.HORIZONTAL ? 135 : appLayoutHeaderOffsetPx,
    width: `calc(100% - ${drawerWidth}px)`,
    [theme.breakpoints.up('md')]: {
      marginTop: menuOrientation === MenuOrientation.HORIZONTAL ? 135 : appLayoutHeaderOffsetPx,
    },
  }),
  [theme.breakpoints.down('md')]: {
    //* באפליקציה: בלי שוליים כפולים — הכרטיס של הדף צמוד למסך
    marginInline: isNative ? 0 : theme.spacing(gridSpacing),
    padding: isNative ? theme.spacing(1) : theme.spacing(gridSpacing),
    marginTop: appLayoutHeaderOffsetPx,
    ...(!open && {
      width: `calc(100% - ${drawerWidth}px)`,
    }),
    '&:has([id="chat-index-main"]), &:has([id="chat-index-sec-main"])': {
      paddingInline: 0,
      paddingBlock: isNative ? theme.spacing(1) : theme.spacing(gridSpacing),
      marginInline: chatGutter,
      marginTop: `${appLayoutHeaderOffsetPx}px`,
      ...(isNative && { borderRadius: 0 }),
    },
  },
  [theme.breakpoints.down('sm')]: {
    marginInline: isNative ? 0 : theme.spacing(gridSpacing),
    '&:has([id="chat-index-main"]), &:has([id="chat-index-sec-main"])': {
      paddingInline: 0,
      paddingBlock: isNative ? theme.spacing(1) : theme.spacing(gridSpacing),
      marginInline: chatGutter,
      marginTop: `${appLayoutHeaderOffsetPx}px`,
      ...(isNative && { borderRadius: 0 }),
    },
  },
  /**
   * מסך צ'אט (טריפלקס) + אשפי תפוצה: גובה מדויק מתחת ל־header — בלי גלילה ב־body, שרשרת flex עם minHeight:0.
   */
  '&:has(#chat-index-main), &:has(#broadcast-wizard-root), &:has(#broadcast-group-wizard-root), &:has(#broadcast-tabs-root)':
    {
      height: chatViewportMinusHeader,
      maxHeight: chatViewportMinusHeader,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      boxSizing: 'border-box',
    },
};
});

export default MainContentStyled;
